import { useEffect, useId, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

type Mode = "login" | "signup" | "reset";

export function AuthModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { signIn, signUp, signInWithGoogle, sendPasswordReset } = useAuth();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, open]);

  if (!open) return null;

  function changeMode(nextMode: Mode) {
    setMode(nextMode);
    setMessage(null);
    setIsError(false);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setMessage(null);
    setIsError(false);

    if (!email.includes("@")) {
      setMessage("Digite um e-mail válido.");
      setIsError(true);
      return;
    }
    if (mode !== "reset" && password.length < 6) {
      setMessage("A senha precisa ter pelo menos 6 caracteres.");
      setIsError(true);
      return;
    }
    if (mode === "signup" && !displayName.trim()) {
      setMessage("Digite como você quer ser chamado.");
      setIsError(true);
      return;
    }

    setBusy(true);
    try {
      if (mode === "reset") {
        const result = await sendPasswordReset(email);
        setIsError(Boolean(result.error));
        setMessage(result.error || "Enviamos as instruções de recuperação. Verifique seu e-mail.");
        return;
      }
      const result = mode === "login" ? await signIn(email, password) : await signUp(email, password, displayName);
      if (result.error) {
        setIsError(true);
        setMessage(translateAuthError(result.error));
        return;
      }
      if (mode === "signup") {
        if (result.authenticated) {
          onClose();
        } else {
          setMessage("Conta criada, mas o Supabase ainda está exigindo confirmação do e-mail. Desative “Confirm email” no provedor de e-mail do projeto.");
        }
      } else {
        onClose();
      }
    } finally {
      setBusy(false);
    }
  }

  async function googleLogin() {
    setBusy(true);
    const result = await signInWithGoogle();
    if (result.error) {
      setIsError(true);
      setMessage(translateAuthError(result.error));
      setBusy(false);
    }
  }

  const title = mode === "login" ? "Entrar na sua conta" : mode === "signup" ? "Criar sua conta" : "Recuperar senha";

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="auth-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <button className="modal-close" type="button" onClick={onClose} aria-label="Fechar">×</button>
        <h2 id={titleId}>{title}</h2>
        <p>{mode === "reset" ? "Informe seu e-mail para receber um link seguro." : "Acesse suas compras em qualquer dispositivo."}</p>

        {mode !== "reset" && <button className="google-button" type="button" onClick={googleLogin} disabled={busy}>Continuar com Google</button>}
        {mode !== "reset" && <div className="form-divider"><span>ou use seu e-mail</span></div>}

        <form onSubmit={submit} noValidate>
          {mode === "signup" && <label>Nome<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoComplete="name" /></label>}
          <label>E-mail<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required /></label>
          {mode !== "reset" && <label>Senha<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={mode === "login" ? "current-password" : "new-password"} required minLength={6} /></label>}
          {message && <p className={isError ? "form-message error" : "form-message success"} role={isError ? "alert" : "status"}>{message}</p>}
          <button className="primary-button auth-submit" type="submit" disabled={busy}>{busy ? "Aguarde…" : mode === "login" ? "Entrar" : mode === "signup" ? "Criar conta" : "Enviar link de recuperação"}</button>
        </form>

        <div className="auth-switches">
          {mode === "login" && <><button type="button" onClick={() => changeMode("reset")}>Esqueci minha senha</button><button type="button" onClick={() => changeMode("signup")}>Ainda não tenho conta</button></>}
          {mode !== "login" && <button type="button" onClick={() => changeMode("login")}>Voltar para entrar</button>}
        </div>
        <p className="legal-copy">Ao continuar, você concorda com os <Link to="/termos" onClick={onClose}>Termos de Uso</Link> e a <Link to="/privacidade" onClick={onClose}>Política de Privacidade</Link>.</p>
      </section>
    </div>
  );
}

function translateAuthError(message: string) {
  if (message.includes("Invalid login credentials")) return "E-mail ou senha incorretos.";
  if (message.includes("User already registered")) return "Este e-mail já possui uma conta.";
  if (message.includes("Password should be")) return "A senha não atende aos requisitos mínimos.";
  return "Não foi possível concluir. Tente novamente em instantes.";
}
